import { useState, useEffect } from "react";
import { Activity, Zap, Upload, RefreshCw, Settings, Radio, AlertCircle } from "lucide-react";
import axios, { API, errorText } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";
import LiveMonitorPanel from "@/components/views/LiveMonitorPanel";

const IoTDataView = ({ clientId, assetUuid }) => {
  const t = useT();
  const [readings, setReadings] = useState([]);
  const [meters, setMeters] = useState([]);
  const [simulating, setSimulating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDept, setUploadDept] = useState("building_facilities");
  const [activeTab, setActiveTab] = useState("readings");
  const [message, setMessage] = useState(null);

  // IoT readings are asset-scoped: ingest requires a building, reads filter by it.
  const assetQuery = assetUuid ? `?asset_uuid=${assetUuid}` : "";

  const fetchReadings = async () => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/iot/latest${assetQuery}`);
      setReadings(response.data);
    } catch (error) {
      console.error("Error fetching IoT readings:", error);
    }
  };

  const fetchMeters = async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/iot/pmc53a/meters${assetQuery}`);
      setMeters(res.data.meters || []);
    } catch (e) {
      console.error("Error fetching meters:", e);
    }
  };

  useEffect(() => { fetchReadings(); fetchMeters(); }, [clientId, assetUuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSimulate = async () => {
    if (!assetUuid) {
      setMessage({ type: "danger", text: "Select an asset before generating IoT data." });
      return;
    }
    setSimulating(true);
    setMessage(null);
    try {
      await axios.post(`${API}/clients/${clientId}/iot/simulate?asset_uuid=${assetUuid}`);
      await fetchReadings();
      await fetchMeters();
    } catch (error) {
      console.error("Error simulating IoT:", error);
      setMessage({ type: "danger", text: errorText(error, "Error generating IoT data") });
    } finally {
      setSimulating(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!assetUuid) {
      setMessage({ type: "danger", text: "Select an asset before uploading meter data." });
      e.target.value = "";
      return;
    }
    setUploading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("department", uploadDept);
      await axios.post(
        `${API}/clients/${clientId}/iot/pmc53a/upload?department=${uploadDept}&asset_uuid=${assetUuid}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      await fetchReadings();
      await fetchMeters();
    } catch (error) {
      console.error("Error uploading PMC data:", error);
      setMessage({ type: "danger", text: errorText(error, "Error uploading meter data") });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deptKeys = ["building_facilities","operations_equipment","transportation_logistics","procurement_waste","administrative","it_infrastructure"];

  return (
    <div data-testid="iot-data-view">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">{t("iot_title")}</h2>
          <p className="page-subtitle">{t("iot_subtitle")}</p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-sm)" }}>
          <button className="btn btn-primary" onClick={handleSimulate} disabled={simulating}>
            <RefreshCw size={18} className={simulating ? "spinning" : ""} />
            {simulating ? t("simulating") : t("simulate")}
          </button>
        </div>
      </div>

      {!assetUuid && (
        <div className="alert alert-warning mb-lg">
          <AlertCircle size={18} />
          <span>Select an asset in the header to ingest or filter IoT data by building.</span>
        </div>
      )}

      {message && (
        <div className={`alert alert-${message.type} mb-lg`}>
          <AlertCircle size={18} />
          <span>{message.text}</span>
        </div>
      )}

      <div className="iot-tabs mb-lg" data-testid="iot-tabs">
        <button className={`iot-tab ${activeTab==="readings"?"active":""}`} onClick={()=>setActiveTab("readings")} data-testid="tab-readings">
          <Activity size={16}/> {t("sensor_readings")} ({readings.length})
        </button>
        <button className={`iot-tab ${activeTab==="upload"?"active":""}`} onClick={()=>setActiveTab("upload")} data-testid="tab-upload">
          <Upload size={16}/> PMC-53A-E {t("upload_data")}
        </button>
        <button className={`iot-tab ${activeTab==="meters"?"active":""}`} onClick={()=>setActiveTab("meters")} data-testid="tab-meters">
          <Settings size={16}/> {t("meters")} ({meters.length})
        </button>
        <button className={`iot-tab ${activeTab==="live"?"active":""}`} onClick={()=>setActiveTab("live")} data-testid="tab-live">
          <Radio size={16}/> {t("live_monitor")}
        </button>
      </div>

      {activeTab === "readings" && (
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">{t("sensor_readings")} ({readings.length})</h4>
          </div>
          {readings.length === 0 ? (
            <div className="flex-center" style={{ padding: "var(--space-xxl)", color: "var(--text-muted)" }}>
              <div style={{ textAlign: "center" }}>
                <Activity size={48} strokeWidth={1} />
                <p className="mt-md">{t("no_iot_readings")}</p>
                <p className="text-muted">{t("click_simulate")}</p>
              </div>
            </div>
          ) : (
            <div className="iot-readings-grid">
              {readings.map((reading, index) => (
                <div className="iot-reading-card" key={reading.sensor_id || index}>
                  <div className="iot-reading-header">
                    <div className="iot-reading-icon"><Zap size={20} /></div>
                    <div className="iot-reading-meta">
                      <div className="iot-reading-type">{reading.sensor_type?.replace(/_/g, " ")}</div>
                      <div className="iot-reading-sensor">{reading.sensor_id}</div>
                    </div>
                  </div>
                  <div className="iot-reading-value-large">
                    {reading.value?.toFixed(2)}
                    <span className="iot-reading-unit-large">{reading.unit}</span>
                  </div>
                  <div className="iot-reading-dept">
                    <span className="badge badge-neutral">{reading.department?.replace(/_/g, " ")}</span>
                    {reading.source && <span className="badge badge-success" style={{marginLeft:4, fontSize:"0.6rem"}}>{reading.source === "file_upload" ? "FILE" : reading.source === "modbus_tcp" ? "LIVE" : "SIM"}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "upload" && (
        <div className="card" data-testid="pmc53a-upload-section">
          <h4 className="card-title">PMC-53A-E {t("upload_data")}</h4>
          <p className="text-muted mb-md" style={{fontSize:"var(--text-small)"}}>
            Upload CSV or Excel files exported from CET PMC-53A-E meters. Expected columns: timestamp, voltage_l1, voltage_l2, voltage_l3, current_l1, current_l2, current_l3, active_power, reactive_power, apparent_power, power_factor, frequency, energy_total, thd_voltage_l1, thd_current_l1, etc.
          </p>

          <div className="form-row mb-lg">
            <div className="form-group">
              <label className="form-label">{t("department")}</label>
              <select className="form-input" value={uploadDept} onChange={e=>setUploadDept(e.target.value)} data-testid="pmc-upload-dept">
                {deptKeys.map(dk => <option key={dk} value={dk}>{dk.replace(/_/g," ")}</option>)}
              </select>
            </div>
          </div>

          <div className="file-upload-zone" style={{ cursor: uploading ? "wait" : "pointer" }} data-testid="pmc-file-drop">
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} style={{display:"none"}} id="pmc-file-input"/>
            <label htmlFor="pmc-file-input" style={{cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:"var(--space-sm)", padding:"var(--space-xl)"}}>
              <div className="file-upload-icon">{uploading ? <div className="spinner"></div> : <Upload size={48} strokeWidth={1.5}/>}</div>
              <div className="file-upload-text">{uploading ? t("uploading") : t("drop_pmc_file")}</div>
              <div className="text-muted" style={{fontSize:"var(--text-small)"}}>CSV, XLSX, XLS</div>
            </label>
          </div>

          <div className="card mt-lg" style={{background:"var(--surface)", border:"1px solid var(--border)"}}>
            <h4 className="card-title" style={{fontSize:"var(--text-small)", textTransform:"uppercase", letterSpacing:"0.05em"}}>{t("pmc_register_map")}</h4>
            <div className="pmc-register-grid">
              {[
                {cat: "Voltage", items: ["voltage_l1","voltage_l2","voltage_l3","voltage_avg","voltage_l12","voltage_l23","voltage_l31"]},
                {cat: "Current", items: ["current_l1","current_l2","current_l3","current_n","current_avg"]},
                {cat: "Power", items: ["active_power","reactive_power","apparent_power","power_factor","frequency"]},
                {cat: "Energy", items: ["energy_import","energy_export","energy_total","reactive_energy_import","reactive_energy_export"]},
                {cat: "THD", items: ["thd_voltage_l1","thd_voltage_l2","thd_voltage_l3","thd_current_l1","thd_current_l2","thd_current_l3"]},
                {cat: "Optional", items: ["analog_input_1","residual_current","rtd_temperature_1","rtd_temperature_2","rtd_temperature_3"]},
              ].map(g => (
                <div key={g.cat} className="pmc-register-group">
                  <div className="pmc-register-cat">{g.cat}</div>
                  {g.items.map(i => <div key={i} className="pmc-register-item">{i}</div>)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "meters" && (
        <div className="card" data-testid="pmc53a-meters-section">
          <h4 className="card-title">PMC-53A-E {t("meters")}</h4>
          {meters.length === 0 ? (
            <p className="text-muted">{t("no_meters")}</p>
          ) : (
            <div className="table-container">
              <table className="table" data-testid="meters-table">
                <thead>
                  <tr>
                    <th>Meter ID</th>
                    <th>{t("department")}</th>
                    <th>{t("source")}</th>
                    <th>{t("readings")}</th>
                    <th>{t("last_reading")}</th>
                  </tr>
                </thead>
                <tbody>
                  {meters.map((m,i) => (
                    <tr key={i}>
                      <td><strong>{m.meter_id}</strong></td>
                      <td>{m.department?.replace(/_/g," ")}</td>
                      <td><span className={`badge badge-${m.source==="simulation"?"neutral":m.source==="file_upload"?"warning":"success"}`}>{m.source || "simulation"}</span></td>
                      <td>{m.reading_count}</td>
                      <td>{m.last_reading ? new Date(m.last_reading).toLocaleString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card mt-lg" style={{borderLeft:"4px solid var(--primary)", background:"var(--surface)"}}>
            <h4 className="card-title" style={{fontSize:"var(--text-small)"}}>Modbus TCP API Integration</h4>
            <p className="text-muted" style={{fontSize:"var(--text-small)"}}>
              For real-time integration, configure your Modbus TCP-to-HTTP bridge to POST readings to:
            </p>
            <code className="mt-sm" style={{display:"block", background:"var(--bg)", padding:"var(--space-md)", borderRadius:"var(--radius)", fontSize:"var(--text-small)", wordBreak:"break-all"}}>
              POST {API}/clients/&#123;client_id&#125;/iot/pmc53a/push
            </code>
            <p className="text-muted mt-sm" style={{fontSize:"var(--text-small)"}}>
              Body: &#123; "meter_id": "XX_PMC53A_01", "department": "building_facilities", "readings": &#123; "voltage_l1": 230.5, ... &#125; &#125;
            </p>
          </div>
        </div>
      )}

      {activeTab === "live" && (
        <LiveMonitorPanel clientId={clientId} assetUuid={assetUuid} />
      )}
    </div>
  );
};

export default IoTDataView;
